import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router';
import { useNavigateWithBreadcrumbs } from '../../hooks';
import i18n from '../../utilities/i18n';
import SitePageHeader from './siteComponents/SiteHeader';
import { Device, Subject } from '../../models';
import { EntityStatus, EntityType } from 'adc-sirius-module-common';
import MessageBanner from '../messageBanner/MessageBanner';
import {
  Button,
  LinkText,
  LoadingSpinner,
  Table,
  VStack,
} from '@adc/parallax-component-library';
import CreateSubjectModal from '../modal/CreateSubjectModal';
import DynamicTable, {
  CheckboxFilterSection,
  ColumnConfig,
} from '../table/DynamicTable';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  createSubjectMutateOptions,
  devicesBySiteQueryOptions,
  siteQueryOptions,
  studyQueryOptions,
  subjectsQueryOptions,
} from '../../queryOptions';
import { updateDeviceMutationOptions } from '../../queryOptions/device';
import { AssignDeviceModal } from '../deviceAssignment/AssignDeviceModal';
import { enumToCheckboxOptions } from '../../utilities/helpers';

/**
 * Site component that displays the details of a specific site.
 * @returns {React.JSX.Element}
 */
export default function Site() {
  const queryClient = useQueryClient();
  const navigateWithBreadcrumbs = useNavigateWithBreadcrumbs();

  const navigateToSubject = (subjectKey: string, subjectLabel: string) => {
    navigateWithBreadcrumbs(
      `/subjects/${subjectKey}`,
      subjectLabel,
      site.data?.name || i18n.t('Page_Site.title'),
    );
  };

  const { siteKey } = useParams();

  const site = useQuery(siteQueryOptions({ key: siteKey }));

  const study = useQuery(studyQueryOptions({ key: site.data?.studyKey }));

  const subjects = useQuery(subjectsQueryOptions({ siteKey: site.data?.key }));

  const [filteredSubjects, setFilteredSubjects] = useState<Subject[]>([]);
  const [subjectStatusFilters, setSubjectStatusFilters] = useState<
    CheckboxFilterSection[]
  >([
    {
      id: 'status',
      label: i18n.t('Entity.status'),
      items: enumToCheckboxOptions(EntityStatus, 'Status'),
    },
  ]);

  useEffect(() => {
    let filteredSubjects = subjects.data || [];

    subjectStatusFilters.forEach((filter) => {
      const activeFilterValues = filter.items
        .filter((item) => item.value)
        .map((item) => item.id);

      if (activeFilterValues.length > 0 && filter.id === 'status') {
        filteredSubjects = filteredSubjects.filter((subject) =>
          activeFilterValues.includes(String(subject.status)),
        );
      }
    });

    setFilteredSubjects(filteredSubjects);
  }, [subjectStatusFilters, subjects.data]);

  // TODO: With CSP-1605, this will be replaced with a call to devices/available
  const devices = useQuery(
    devicesBySiteQueryOptions(
      {
        key: siteKey,
      },
      { enabled: !!siteKey },
    ),
  );

  const assignDevice = useMutation(updateDeviceMutationOptions());

  const createSubject = useMutation(
    createSubjectMutateOptions({
      onSuccess: (subject) => {
        queryClient.setQueryData(
          subjectsQueryOptions({ siteKey: site.data?.key }).queryKey,
          (subjects: Subject[] | undefined) => [...(subjects ?? []), subject],
        );
      },
    }),
  );

  const getStatusLabel = (status: EntityStatus) => {
    return i18n.t(`Status.${EntityStatus[status].toLowerCase()}`);
  };

  const subjectsTableColumnData: ColumnConfig[] = [
    { label: i18n.t('Entity.name'), dataKey: 'name' },
    {
      label: i18n.t('Entity.status'),
      dataKey: 'status',
      getSortValue: (subject: Subject) => getStatusLabel(subject.status),
    },
  ];

  const subjectsTableCellRenderer = (dataKey: string, subject: Subject) => {
    if (dataKey === 'name') {
      return (
        <Table.TextCell key={dataKey}>
          <LinkText
            onPress={() => navigateToSubject(subject.key, subject.name)}
            type="$bodySmall.default"
          >
            {subject[dataKey]}
          </LinkText>
        </Table.TextCell>
      );
    } else if (dataKey === 'status') {
      return (
        <Table.TextCell key={dataKey}>
          {getStatusLabel(subject.status)}
        </Table.TextCell>
      );
    } else {
      return <Table.TextCell key={dataKey}>{subject[dataKey]}</Table.TextCell>;
    }
  };

  const devicesTableColumnData: ColumnConfig[] = [
    { label: i18n.t('Device Name'), dataKey: 'appName' },
    { label: i18n.t('Entity.id'), dataKey: 'deviceId' },
    { label: i18n.t('Device.os'), dataKey: 'os' },
    { label: i18n.t('Device.model'), dataKey: 'phoneModel' },
  ];

  return (
    <VStack gap={'$4'} height={'100%'}>
      {site.isPending || study.isPending ? (
        <VStack alignItems="center" justifyContent="center" flexGrow={1}>
          <LoadingSpinner testID="site-loading-spinner" />
        </VStack>
      ) : null}

      {site.isError || study.isError ? (
        <MessageBanner
          type="danger"
          headerText={i18n.t('Page_Site.load_error_title')}
          bodyText={i18n.t('Page_Site.load_error')}
        />
      ) : null}

      {site.isSuccess && study.isSuccess ? (
        <VStack gap="$4">
          <SitePageHeader site={site.data} study={study.data} />

          <DynamicTable
            isLoading={subjects.isPending}
            hasLoadingError={subjects.isError}
            title={i18n.t('Page_Site.table_subjects_title')}
            description={i18n.t('Page_Site.table_subjects_description')}
            columnData={subjectsTableColumnData}
            rowData={filteredSubjects}
            cellRenderer={subjectsTableCellRenderer}
            enableFiltering={true}
            checkboxFilters={subjectStatusFilters}
            onFiltersChange={setSubjectStatusFilters}
            onFiltersReset={() => {
              setSubjectStatusFilters((prevFilters) =>
                prevFilters.map((filter) => ({
                  ...filter,
                  items: filter.items.map((item) => ({
                    ...item,
                    value: false,
                  })),
                })),
              );
            }}
          >
            <CreateSubjectModal
              studyKey={site.data.studyKey}
              sites={[site.data]}
              trigger={
                <Button
                  size="medium"
                  label={i18n.t('Page_Site.add_subject')}
                  onPress={() => {}}
                  disabled={createSubject.isPending}
                />
              }
              onSuccess={async (newSubject: Subject, device?: Device) => {
                await createSubject
                  .mutateAsync({ subject: newSubject })
                  .then((subject) => {
                    if (device) {
                      return assignDevice
                        .mutateAsync({
                          deviceKey: device.key,
                          updatedDeviceValues: {
                            ...device,
                            siteKey: undefined, // Unassign from site
                            subjectKey: subject.key,
                          },
                        })
                        .then((device) =>
                          // remove the newly assigned device from list of unassigned devices for site
                          queryClient.setQueryData(
                            devicesBySiteQueryOptions({
                              key: site.data?.key,
                            }).queryKey,
                            (devices: Device[] | undefined) =>
                              devices?.filter((d) => d.key !== device.key),
                          ),
                        );
                    }
                  });
              }}
            />
          </DynamicTable>

          <DynamicTable
            isLoading={devices.isPending}
            hasLoadingError={devices.isError}
            title={i18n.t('Page_Site.table_devices_title')}
            description={i18n.t('Page_Site.table_devices_description')}
            columnData={devicesTableColumnData}
            rowData={devices.data || []}
          >
            <AssignDeviceModal
              entityType={EntityType.Site}
              entityKey={site.data.key}
              entityName={site.data.name}
              trigger={
                <Button
                  size="medium"
                  label={i18n.t('Page_Site.manage_assignments')}
                  onPress={() => {}}
                  disabled={devices.isPending}
                />
              }
            />
          </DynamicTable>
        </VStack>
      ) : null}
    </VStack>
  );
}
